import { el, clear, toast } from '../core/dom.js';
import { activeHabits, getState, loadState, planFor } from '../core/state.js';
import { formatDuration, formatHm, localDate, minutesSinceLocalMidnight, nowIso, tzOffsetMinutes } from '../core/time.js';
import {
  consumptionMethods, events as eventsStore, logEvent, markEventUndone,
  metaGet, moneyModels, planStates, purchaseTemplates as purchaseTemplatesStore,
} from '../storage/db.js';
import { showUndoToast } from '../components/undo-toast.js';
import { getActiveEpisode, startEpisode } from '../episodes/episode-store.js';
import { createHeroActionCard } from '../components/hero-action-card.js';
import { effectiveLifecycle, resumePlan } from '../plans/lifecycle.js';
import { habitDisplayLabel } from '../core/display.js';
import { promoteCalibrationPlan } from '../plans/adaptation.js';
import { pickTuneUpCard } from '../onboarding/tune-up-cards.js';
import { renderIntentionCard, renderCloseoutCard } from '../onboarding/daily-rituals.js';
import { pickBackfillTime } from '../components/backfill-picker.js';
import { pendingCoolingOff, resolveCoolingOff } from '../progress/purchase-log.js';
import type { CoolingOffPayload } from '../progress/purchase-log.js';
import type { EpisodeRecord } from '../core/types.js';
import { applyUsageNormalized as intervalApplyNormalized, extendSession as intervalExtendSession } from '../rules/interval-engine.js';
import { applyUsageNormalized as quotaApplyNormalized, closePeriodIfDue } from '../rules/quota-engine.js';
import { applyUsage as windowApply, closeDayIfNeeded } from '../rules/window-engine.js';
import { applySecondaryUsage, ensureSecondaryRolled } from '../rules/hybrid-engine.js';
import { classifyNewUsage, normalizeUsageEvents } from '../rules/usage-normalizer.js';
import { renderPurchaseLogSheet } from './purchase-log-sheet.js';
import { uuid } from '../core/ids.js';
import { navigate } from '../router.js';
import { copy } from '../copy/tone.js';
import type { ToneKey } from '../copy/tone.js';
import type {
  ConsumptionMethodRecord, HabitRecord, IntervalState, PlanRecord, PlanStateRecord,
  PurchaseTemplateRecord, QuotaState, UsageLoggedPayloadV2, WindowState,
} from '../core/types.js';

let timerHandle: number | null = null;

export async function renderToday(parent: HTMLElement) {
  const habits = activeHabits();
  if (habits.length === 0) {
    parent.appendChild(el('p', {}, 'No active plan.'));
    return;
  }

  // Roll period/day for all plans up front
  for (const h of habits) {
    const { plan, planState } = planFor(h.id);
    if (!plan || !planState) continue;
    let psr = planState;
    const rolled = ensureSecondaryRolled(psr);
    if (rolled !== psr) { await planStates.put(rolled); psr = rolled; }
    if (psr.state.planType === 'quota') {
      const closed = closePeriodIfDue(psr.state, plan.pace);
      if (closed.closed) await planStates.put({ ...psr, state: closed.state, updatedAt: nowIso() });
    }
    if (psr.state.planType === 'window') {
      const closed = closeDayIfNeeded(psr.state);
      if (closed.closed) await planStates.put({ ...psr, state: closed.state, updatedAt: nowIso() });
    }
  }
  // Silent reload of state cache (no listener fan-out, so we don't replace
  // the very DOM nodes we're about to paint into).
  await loadState();
  const st = getState();
  const refreshedHabits = activeHabits(st);

  clear(parent);
  parent.appendChild(headerBar());
  const reminder = await maybeBackupReminder(refreshedHabits.map((h) => h.id));
  if (reminder) parent.appendChild(reminder);

  const methodsByHabit = new Map<string, ConsumptionMethodRecord[]>();
  const templatesByHabit = new Map<string, PurchaseTemplateRecord[]>();
  for (const h of refreshedHabits) {
    methodsByHabit.set(h.id, (await consumptionMethods.byHabit(h.id))
      .filter((m) => m.active)
      .sort((a, b) => a.sortOrder - b.sortOrder));
    templatesByHabit.set(h.id, (await purchaseTemplatesStore.byHabit(h.id)).filter((t) => t.active));
  }
  const recentByHabit = new Map<string, import('../core/types.js').AppEvent[]>();
  for (const h of refreshedHabits) {
    recentByHabit.set(h.id, await eventsStore.byHabit(h.id));
  }

  const defaultHabitId = st.activeHabit?.id;
  const ordered = [...refreshedHabits].sort((a, b) => {
    if (a.id === defaultHabitId) return -1;
    if (b.id === defaultHabitId) return 1;
    return 0;
  });

  const carousel = el('div', {
    class: 'plan-carousel',
    attrs: { role: 'region', 'aria-label': 'Active plans' },
  });
  for (const h of ordered) {
    const slide = await buildPlanSlide(h, methodsByHabit, templatesByHabit, recentByHabit, st);
    if (slide) carousel.appendChild(slide);
  }
  if (ordered.length > 1) parent.appendChild(buildDots(ordered, carousel));
  parent.appendChild(carousel);

  parent.appendChild(el('button', {
    class: 'btn block',
    attrs: { style: 'margin-top: var(--space-4);' },
    onclick: () => {
      const activeCount = refreshedHabits.length;
      if (activeCount >= 1) {
        const ok = confirm(
          'Working on many things at once can be hard. Start as a light tracker first?\n\nYou can promote it to a full plan later from the Plan tab.',
        );
        if (!ok) return;
      }
      navigate('/onboard');
    },
  }, '+ Add another plan'));

  if (timerHandle) window.clearInterval(timerHandle);
  timerHandle = window.setInterval(() => {
    // Self-cancel if the Now screen is no longer the visible route — prevents
    // the tick from running against a detached DOM after the user navigates.
    if (!location.hash.startsWith('#/today')) {
      if (timerHandle !== null) { window.clearInterval(timerHandle); timerHandle = null; }
      return;
    }
    updateLive(parent);
  }, 1000);
}

async function buildPlanSlide(
  habit: HabitRecord,
  methodsByHabit: Map<string, ConsumptionMethodRecord[]>,
  templatesByHabit: Map<string, PurchaseTemplateRecord[]>,
  recentByHabit: Map<string, import('../core/types.js').AppEvent[]>,
  st: ReturnType<typeof getState>,
): Promise<HTMLElement | null> {
  const { plan, planState } = planFor(habit.id, st);
  if (!plan || !planState) return null;
  const methods = methodsByHabit.get(habit.id) ?? [];
  const templates = templatesByHabit.get(habit.id) ?? [];
  const recent = recentByHabit.get(habit.id) ?? [];

  const slide = el('section', {
    class: 'plan-slide',
    dataset: { habitId: habit.id },
    attrs: { 'aria-label': displayLabel(habit) },
  });

  const isDefault = st.meta.active_habit_id === habit.id;
  slide.appendChild(el('div', {
    class: 'plan-slide-head',
    attrs: { style: 'display:flex;align-items:baseline;justify-content:space-between;gap:0.5rem;margin:0 0 0.5rem;' },
  },
    el('h2', { attrs: { style: 'margin:0;font-size:1rem;color:var(--text-muted);text-transform:uppercase;letter-spacing:0.05em;' } },
      `${displayLabel(habit)}${isDefault ? ' · default' : ''}`),
    el('a', { href: `#/plan?habit=${habit.id}`, class: 'tag' }, 'Edit'),
  ));

  const lc = effectiveLifecycle(plan);
  let replacementHero: HTMLElement | null = null;
  if (lc === 'paused') replacementHero = buildPausedHero(habit, plan);
  else if (lc === 'graduated') replacementHero = buildGraduatedHero(habit);
  else {
    const activeEpisode = await getActiveEpisode(habit.id);
    if (activeEpisode && !activeEpisode.endedAt) {
      replacementHero = buildResumeUrgeHero(activeEpisode, habit);
    }
  }

  if (replacementHero) {
    slide.appendChild(replacementHero);
  } else {
    slide.appendChild(targetCard(habit, planState));
    if (planState.secondaryQuota) slide.appendChild(secondaryQuotaCard(planState.secondaryQuota));
    const sessionCard = activeSessionCard(methods, recent);
    if (sessionCard) slide.appendChild(sessionCard);
    slide.appendChild(buildActionRow(habit, plan, planState, methods, templates));
  }

  const intention = renderIntentionCard(habit);
  if (intention.node) slide.appendChild(intention.node);

  slide.appendChild(snapshotCard(planState.state));

  const calibration = buildCalibrationCard(habit, plan, planState, recent);
  if (calibration) slide.appendChild(calibration);

  const pending = pendingCoolingOff(recent);
  if (pending.length > 0) slide.appendChild(buildPendingDecisionsCard(habit.id, pending));

  const closeout = renderCloseoutCard(habit);
  if (closeout.node) slide.appendChild(closeout.node);

  const moneyModel = await moneyModels.get(habit.id);
  const tuneUp = await pickTuneUpCard({
    habit,
    recentEvents: recent,
    ...(moneyModel ? { money: moneyModel } : { money: undefined }),
    settings: st.settings,
    ...(st.meta.onboarding_metadata ? { metadata: st.meta.onboarding_metadata } : { metadata: undefined }),
  });
  if (tuneUp) slide.appendChild(tuneUp);

  return slide;
}

function buildActionRow(
  habit: HabitRecord,
  plan: PlanRecord,
  ps: PlanStateRecord,
  methods: ConsumptionMethodRecord[],
  templates: PurchaseTemplateRecord[],
): HTMLElement {
  const card = el('section', { class: 'card plan-actions', attrs: { 'aria-label': 'Quick actions' } });

  card.appendChild(el('button', {
    class: 'btn urge block',
    onclick: () => startUrgeFromHero(habit),
  }, "I'm having an urge"));

  const unit = habit.unitSingular;
  const grid = el('div', { class: 'method-grid', attrs: { style: 'margin-top: var(--space-3);' } });
  if (methods.length === 0) {
    grid.appendChild(el('button', {
      class: 'btn soft',
      onclick: () => onLog(habit, plan, ps, 'used', undefined),
    }, `Log ${unit}`));
  } else {
    for (const m of methods) {
      grid.appendChild(el('button', {
        class: 'btn soft',
        onclick: () => onLog(habit, plan, ps, 'used', m),
      }, `Log ${m.label}`));
    }
  }
  card.appendChild(grid);

  const primary = methods.find((m) => m.active);
  card.appendChild(el('button', {
    class: 'btn ghost block',
    attrs: { style: 'margin-top: var(--space-3);' },
    onclick: async () => {
      const when = await pickBackfillTime();
      if (!when) return;
      await onLog(habit, plan, ps, 'used', primary, when);
    },
  }, 'Log earlier'));

  if (templates.length > 0) {
    card.appendChild(el('button', {
      class: 'btn ghost block',
      attrs: { style: 'margin-top: var(--space-3);' },
      onclick: () => renderPurchaseLogSheet(habit, methods, templates),
    }, 'Log a purchase'));
  }

  return card;
}

function buildDots(habits: HabitRecord[], carousel: HTMLElement): HTMLElement {
  const wrap = el('div', { class: 'plan-dots', attrs: { role: 'tablist', 'aria-label': 'Plan selector' } });
  habits.forEach((h, i) => {
    const dot = el('button', {
      class: i === 0 ? 'plan-dot active' : 'plan-dot',
      attrs: {
        type: 'button',
        role: 'tab',
        'aria-label': `Plan ${i + 1}: ${displayLabel(h)}`,
        'aria-selected': i === 0 ? 'true' : 'false',
      },
      onclick: () => {
        const slide = carousel.children[i] as HTMLElement | undefined;
        if (slide) carousel.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
      },
    });
    wrap.appendChild(dot);
  });
  carousel.addEventListener('scroll', () => {
    const w = carousel.clientWidth;
    if (w === 0) return;
    const idx = Math.round(carousel.scrollLeft / w);
    [...wrap.children].forEach((d, i) => {
      const dot = d as HTMLElement;
      const on = i === idx;
      dot.classList.toggle('active', on);
      dot.setAttribute('aria-selected', on ? 'true' : 'false');
    });
  }, { passive: true });
  return wrap;
}

function buildPausedHero(habit: HabitRecord, plan: PlanRecord): HTMLElement {
  const until = plan.pausedUntil ? new Date(plan.pausedUntil).toLocaleDateString() : null;
  const card = createHeroActionCard({
    state: 'normal',
    eyebrow: `${displayLabel(habit)} · paused`,
    title: 'Plan paused.',
    subtitle: until
      ? `Resumes ${until}. Rescue still available any time.`
      : 'Resume when you\'re ready. Rescue still available.',
    primary: {
      label: 'Resume plan',
      onClick: async () => {
        await resumePlan(plan);
        window.dispatchEvent(new CustomEvent('wean:state-changed'));
      },
    },
    secondary: [
      { label: 'Use rescue', variant: 'urge', onClick: () => startUrgeFromHero(habit) },
    ],
  });
  return card.root;
}

function buildGraduatedHero(habit: HabitRecord): HTMLElement {
  const card = createHeroActionCard({
    state: 'normal',
    eyebrow: `${displayLabel(habit)} · graduated`,
    title: 'You hit this goal.',
    subtitle: 'Rescue stays available. Log if a tough moment shows up.',
    primary: { label: 'Use rescue', variant: 'urge', onClick: () => startUrgeFromHero(habit) },
    secondary: [
      { label: 'Move to maintenance', onClick: () => navigate('/plan', { habit: habit.id, section: 'schedule' }) },
    ],
  });
  return card.root;
}

function buildCalibrationCard(
  habit: HabitRecord,
  plan: PlanRecord,
  planState: PlanStateRecord,
  events: import('../core/types.js').AppEvent[],
): HTMLElement | null {
  if (plan.origin !== 'calibration') return null;
  if (plan.lifecycleState && plan.lifecycleState !== 'calibration' && plan.lifecycleState !== 'active') return null;
  const daysElapsed = (Date.now() - new Date(plan.createdAt).getTime()) / 86_400_000;
  if (daysElapsed < 3) return null;
  const usageCount = events.filter((e) => e.type === 'usage_logged' && e.status !== 'undone').length;
  if (usageCount < 2) return null;
  const card = el('section', {
    class: 'card',
    attrs: { 'aria-label': 'Calibration complete', style: 'border-color:var(--accent-soft);margin-bottom:1rem;' },
  });
  card.appendChild(el('div', {
    class: 'muted',
    attrs: { style: 'text-transform:uppercase;letter-spacing:0.06em;font-size:0.72rem;margin-bottom:0.35rem;' },
  }, 'Calibration ready'));
  card.appendChild(el('h2', { attrs: { style: 'margin:0;font-size:1.05rem;' } },
    `${Math.floor(daysElapsed)} days of honest logging.`));
  card.appendChild(el('p', { class: 'muted', attrs: { style: 'margin:0.35rem 0;font-size:0.9rem;' } },
    'Wean can suggest a real first-week target based on your pattern.'));
  card.appendChild(el('div', { class: 'row', attrs: { style: 'gap:0.5rem;flex-wrap:wrap;' } },
    el('button', {
      class: 'btn small primary', attrs: { type: 'button' },
      onclick: async () => {
        await promoteCalibrationPlan(plan, planState, events);
        toast('Plan set. You can tune anytime.');
        window.dispatchEvent(new CustomEvent('wean:state-changed'));
      },
    }, 'Set my first target'),
    el('button', {
      class: 'btn small ghost', attrs: { type: 'button' },
      onclick: () => navigate('/plan', { habit: habit.id, section: 'schedule' }),
    }, 'Tune by hand'),
  ));
  return card;
}

function buildPendingDecisionsCard(habitId: string, pending: CoolingOffPayload[]): HTMLElement {
  const card = el('section', {
    class: 'card', attrs: { 'aria-label': 'Pending decisions', style: 'border-color:var(--accent-soft);margin-bottom:1rem;' },
  });
  card.appendChild(el('div', {
    class: 'muted',
    attrs: { style: 'text-transform:uppercase;letter-spacing:0.06em;font-size:0.72rem;margin-bottom:0.35rem;' },
  }, 'Cool-off decisions ready'));
  for (const p of pending) {
    const row = el('div', { class: 'list-row' },
      el('div', {},
        el('div', { class: 'label' }, `${p.label} · ${p.currency} ${p.amount.toFixed(2)}`),
        el('div', { class: 'sub muted', attrs: { style: 'font-size:0.8rem;' } },
          'Logged 24h+ ago. What happened?'),
      ),
      el('div', { class: 'row', attrs: { style: 'gap:0.4rem;flex-wrap:wrap;' } },
        el('button', {
          class: 'btn small ghost', attrs: { type: 'button' },
          onclick: async () => {
            await resolveCoolingOff(habitId, p, 'bought');
            toast('Logged purchase.');
            window.dispatchEvent(new CustomEvent('wean:state-changed'));
          },
        }, 'I bought it'),
        el('button', {
          class: 'btn small primary', attrs: { type: 'button' },
          onclick: async () => {
            await resolveCoolingOff(habitId, p, 'skipped');
            toast(`Protected ${p.currency} ${p.amount.toFixed(2)}.`);
            window.dispatchEvent(new CustomEvent('wean:state-changed'));
          },
        }, 'I skipped it'),
        el('button', {
          class: 'btn small ghost', attrs: { type: 'button' },
          onclick: async () => {
            await resolveCoolingOff(habitId, p, 'still_deciding');
            toast('Saved — we will not nag.');
            window.dispatchEvent(new CustomEvent('wean:state-changed'));
          },
        }, 'Still deciding'),
      ),
    );
    card.appendChild(row);
  }
  return card;
}

function displayLabel(habit: HabitRecord): string {
  return habitDisplayLabel(habit);
}

function buildResumeUrgeHero(ep: EpisodeRecord, habit: HabitRecord): HTMLElement {
  const elapsed = Date.now() - new Date(ep.startedAt).getTime();
  const card = createHeroActionCard({
    state: 'urge_active',
    eyebrow: `${displayLabel(habit)} · urge support`,
    title: 'Urge support is still open.',
    liveValue: compactDuration(elapsed),
    subtitle: 'Return to keep the timer and rescue options in one place.',
    primary: { label: 'Return to support', variant: 'urge', onClick: () => navigate(`/urge/${ep.id}`) },
    secondary: [
      { label: 'Log my use', onClick: () => navigate(`/urge/${ep.id}`) },
    ],
  });
  return card.root;
}

async function startUrgeFromHero(habit: HabitRecord): Promise<void> {
  const st = getState();
  const { plan } = planFor(habit.id, st);
  const targetWaitMs = plan && st.activePlanState?.state.planType === 'interval'
    ? st.activePlanState.state.targetIntervalMs
    : undefined;
  const existing = await getActiveEpisode(habit.id);
  const triggers = st.settings.triggers && st.settings.triggers.length > 0 ? st.settings.triggers : undefined;
  const ep = existing ?? await startEpisode({
    habitId: habit.id,
    ...(plan ? { planId: plan.id } : {}),
    kind: 'urge',
    ...(targetWaitMs !== undefined ? { targetWaitMs } : {}),
    ...(triggers ? { triggers } : {}),
  });
  if (!existing) {
    await logEvent({
      type: 'urge_episode_started',
      habitId: habit.id,
      ...(plan ? { planId: plan.id } : {}),
      episodeId: ep.id,
      occurredAt: ep.startedAt,
      localDate: localDate(new Date(ep.startedAt)),
      timezoneOffsetMinutes: tzOffsetMinutes(new Date(ep.startedAt)),
      payload: {
        episodeId: ep.id,
        ...(targetWaitMs !== undefined ? { targetWaitMs } : {}),
        ...(triggers ? { triggers } : {}),
      },
    });
  }
  navigate(`/urge/${ep.id}`);
}

function headerBar(): HTMLElement {
  const habits = activeHabits();
  return el('header', { class: 'app-header' },
    el('div', { class: 'brand' }, el('span', { class: 'brand-dot' }), 'Wean'),
    el('div', {},
      el('span', { class: 'chip' }, el('span', { class: 'dot' }), `${habits.length} active`),
    )
  );
}

function activeSessionCard(
  methods: ConsumptionMethodRecord[],
  recentEvents: import('../core/types.js').AppEvent[],
): HTMLElement | null {
  if (methods.length === 0) return null;
  const sessionMethods = methods.filter((m) => m.defaultLogMode !== 'instant');
  if (sessionMethods.length === 0) return null;
  const cutoff = new Date(Date.now() - 6 * 3600_000).toISOString();
  // Include usage_session_ended events so the normalizer can apply the manual
  // end-now override; without them the session would always look active.
  const recentForNormalizer = recentEvents.filter((e) =>
    (e.type === 'usage_logged' && e.occurredAt >= cutoff)
    || (e.type === 'usage_session_ended' && e.occurredAt >= cutoff),
  );
  const normalized = normalizeUsageEvents(recentForNormalizer, methods);
  const now = Date.now();
  const active = normalized
    .filter((u) => u.logMode !== 'instant' && new Date(u.effectiveEndAt).getTime() > now)
    .sort((a, b) => b.effectiveEndAt.localeCompare(a.effectiveEndAt));
  if (active.length === 0) return null;
  const u = active[0];
  if (!u) return null;
  const method = methods.find((m) => m.id === u.methodId);
  if (!method) return null;
  const remainingMs = new Date(u.effectiveEndAt).getTime() - now;
  const refEventId = u.eventIds[u.eventIds.length - 1] ?? '';
  const card = el('div', {
    class: 'card active-session',
    dataset: {
      activeSession: '1',
      habitId: u.habitId,
      methodId: u.methodId ?? '',
      endsAt: u.effectiveEndAt,
      refEventId,
    },
  },
    el('div', { class: 'pulse-dot' }),
    el('div', { class: 'body' },
      el('div', { class: 'label' }, `${method.label} session active`),
      el('div', { class: 'remaining', dataset: { live: 'remaining' } }, `Ends in ${formatDuration(remainingMs)}`),
    ),
    el('button', {
      class: 'btn ghost',
      onclick: async () => {
        await endSession(u.habitId, refEventId, method);
      },
    }, 'End now'),
  );
  return card;
}

async function endSession(
  habitId: string,
  refEventId: string,
  method: ConsumptionMethodRecord,
): Promise<void> {
  const now = new Date();
  const endedAtIso = now.toISOString();
  await logEvent({
    type: 'usage_session_ended',
    habitId,
    occurredAt: endedAtIso,
    localDate: localDate(now),
    timezoneOffsetMinutes: tzOffsetMinutes(now),
    payload: {
      refEventId,
      methodId: method.id,
      methodKind: method.kind,
      endedAt: endedAtIso,
      reason: 'manual',
    },
  });

  // Pull lastUseAt back if it was projected past the manual end. Keeps the
  // interval timer honest for plans where this session's projected end set it.
  const st = getState();
  const { plan, planState } = planFor(habitId, st);
  if (plan && planState && planState.state.planType === 'interval') {
    const lastUseAt = planState.state.lastUseAt;
    if (lastUseAt && new Date(lastUseAt).getTime() > now.getTime()) {
      const adjusted: PlanStateRecord = {
        ...planState,
        state: { ...planState.state, lastUseAt: endedAtIso },
        updatedAt: nowIso(),
      };
      await planStates.put(adjusted);
    }
  }

  toast(`${method.label} session ended.`);
  window.dispatchEvent(new CustomEvent('wean:state-changed'));
}

function targetCard(habit: HabitRecord, ps: PlanStateRecord): HTMLElement {
  const state = ps.state;
  if (state.planType === 'interval') {
    return intervalRingCard(habit, state);
  }
  if (state.planType === 'window') {
    const minsNow = minutesSinceLocalMidnight(new Date());
    const target = state.targetFirstUseMinutes;
    const status = state.todayFirstUseMinutes !== undefined
      ? `First use logged at ${formatHm(state.todayFirstUseMinutes)}.`
      : minsNow < target
        ? `Hold until ${formatHm(target)}.`
        : `Window open. You hit the target.`;
    return el('div', { class: 'card', dataset: { kind: 'window', habitId: habit.id } },
      el('h2', {}, "Today's target"),
      el('div', { class: 'big' }, `First use after ${formatHm(target)}`),
      el('div', { class: 'muted' }, status),
      state.lastExplanation ? el('p', { class: 'muted' }, state.lastExplanation) : null,
    );
  }
  const left = Math.max(0, state.currentQuota - state.usedThisPeriod);
  return el('div', { class: 'card', dataset: { kind: 'quota', habitId: habit.id } },
    el('h2', {}, `${state.period.charAt(0).toUpperCase()}${state.period.slice(1)} allowance`),
    el('div', { class: 'big' }, `${state.usedThisPeriod} of ${state.currentQuota} used`),
    el('div', { class: 'muted' }, `${left} left this period.`),
    state.lastExplanation ? el('p', { class: 'muted' }, state.lastExplanation) : null,
  );
}

function intervalRingCard(habit: HabitRecord, state: IntervalState): HTMLElement {
  const elapsed = state.lastUseAt ? Date.now() - new Date(state.lastUseAt).getTime() : null;
  const target = state.targetIntervalMs;
  const remain = elapsed === null ? target : Math.max(0, target - elapsed);
  const ratio = elapsed === null ? 0 : Math.min(1, elapsed / target);
  const reached = remain === 0;

  // Build SVG ring inline (we own currentColor + gradient defs).
  const ns = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(ns, 'svg');
  svg.setAttribute('viewBox', '0 0 120 120');
  svg.setAttribute('aria-hidden', 'true');
  const defs = document.createElementNS(ns, 'defs');
  defs.innerHTML = `<linearGradient id="weanGrad" x1="0" y1="0" x2="1" y2="1">
    <stop offset="0%" stop-color="#5eead4"/>
    <stop offset="100%" stop-color="#67e8f9"/>
  </linearGradient>`;
  const r = 50;
  const cx = 60, cy = 60;
  const c = 2 * Math.PI * r;
  const track = document.createElementNS(ns, 'circle');
  track.setAttribute('class', 'track');
  track.setAttribute('cx', String(cx)); track.setAttribute('cy', String(cy)); track.setAttribute('r', String(r));
  track.setAttribute('stroke-width', '10');
  const fill = document.createElementNS(ns, 'circle');
  fill.setAttribute('class', 'fill');
  fill.setAttribute('cx', String(cx)); fill.setAttribute('cy', String(cy)); fill.setAttribute('r', String(r));
  fill.setAttribute('stroke-width', '10');
  fill.setAttribute('stroke-dasharray', String(c));
  fill.setAttribute('stroke-dashoffset', String(c * (1 - ratio)));
  fill.dataset['circ'] = String(c);
  svg.appendChild(defs); svg.appendChild(track); svg.appendChild(fill);

  const ringWrap = el('div', { class: `target-ring${reached ? ' reached' : ''}`, dataset: { role: 'ring' } });
  ringWrap.appendChild(svg);
  const center = el('div', { class: 'center' },
    el('div', { class: 'time', dataset: { live: 'time' } }, reached ? 'GO' : compactDuration(remain)),
    el('div', { class: 'label' }, reached ? 'target reached' : 'remaining'),
  );
  ringWrap.appendChild(center);

  const meta = el('div', { class: 'meta' },
    el('div', { class: 'h' }, 'Current target'),
    el('div', { class: 'summary' }, `Wait ${formatDuration(target)}`),
    el('div', { class: 'note muted', dataset: { live: 'note' } }, state.lastUseAt
      ? `Elapsed ${formatDuration(elapsed ?? 0)}`
      : 'No use logged yet — start whenever.'),
    state.lastExplanation
      ? el('div', { class: 'note muted', attrs: { style: 'font-size:0.78rem; opacity:0.8;' } }, state.lastExplanation)
      : null,
  );

  return el('div', {
    class: 'card target-ring-card no-grad',
    dataset: { kind: 'interval', habitId: habit.id },
  }, ringWrap, meta);
}

function relativeAgo(ms: number): string {
  const min = Math.round(ms / 60_000);
  if (min < 60) return `${min}m`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h}h`;
  const d = Math.round(h / 24);
  return `${d}d`;
}

function compactDuration(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  return `${m}:${String(sec).padStart(2, '0')}`;
}

function secondaryQuotaCard(q: QuotaState): HTMLElement {
  const left = Math.max(0, q.currentQuota - q.usedThisPeriod);
  return el('div', { class: 'card', dataset: { kind: 'secondary' } },
    el('h2', {}, `${q.period.charAt(0).toUpperCase()}${q.period.slice(1)} cap`),
    el('div', { class: 'big' }, `${q.usedThisPeriod} of ${q.currentQuota} used`),
    el('div', { class: 'muted' }, `${left} left.`),
  );
}

function snapshotCard(state: IntervalState | QuotaState | WindowState): HTMLElement {
  if (state.planType === 'interval') {
    return el('div', { class: 'card' },
      el('h2', {}, 'Snapshot'),
      el('div', { class: 'row' },
        kpi('Streak', String(state.successStreak)),
        kpi('Slips', String(state.slipStreak)),
        kpi('Momentum', String(state.momentum)),
      ),
    );
  }
  if (state.planType === 'window') {
    return el('div', { class: 'card' },
      el('h2', {}, 'Snapshot'),
      el('div', { class: 'row' },
        kpi('Success days', String(state.successDays)),
        kpi('Miss days', String(state.missDays)),
      ),
    );
  }
  return el('div', { class: 'card' },
    el('h2', {}, 'Snapshot'),
    el('div', { class: 'row' },
      kpi('Used', String(state.usedThisPeriod)),
      kpi('Cap', String(state.currentQuota)),
      kpi('Periods on track', String(state.successPeriods)),
    ),
  );
}

function kpi(label: string, value: string): HTMLElement {
  return el('div', { class: 'kpi' },
    el('div', { class: 'label' }, label),
    el('div', { class: 'value' }, value),
  );
}

async function maybeBackupReminder(habitIds: string[]): Promise<HTMLElement | null> {
  if (!getState().settings.backupRemindersEnabled) return null;
  const last = await metaGet('last_backup_at');
  let meaningful = 0;
  for (const id of habitIds) {
    const ev = await eventsStore.byHabit(id);
    meaningful += ev.filter((e) => e.type === 'usage_logged' || e.type === 'success_logged').length;
  }
  if (meaningful < 5) return null;
  const stale = !last || (Date.now() - new Date(last).getTime()) > 14 * 86400_000;
  if (!stale) return null;
  return el('div', { class: 'card', attrs: { style: 'border-color: var(--warn);' } },
    el('div', {}, 'Backup reminder: your plan lives on this device.'),
    el('a', { href: '#/backup', class: 'btn ghost', attrs: { style: 'margin-top:0.5rem;' } }, 'Export now'),
  );
}

function updateLive(parent: HTMLElement) {
  // Active session countdown(s)
  const sessions = parent.querySelectorAll('[data-active-session="1"]');
  for (const card of [...sessions]) {
    const node = card as HTMLElement;
    const endsAt = node.dataset['endsAt'];
    if (!endsAt) continue;
    const remainingMs = new Date(endsAt).getTime() - Date.now();
    if (remainingMs <= 0) {
      node.remove();
      continue;
    }
    const live = node.querySelector('[data-live="remaining"]') as HTMLElement | null;
    if (live) live.textContent = `Ends in ${formatDuration(remainingMs)}`;
  }

  const cards = parent.querySelectorAll('[data-kind="interval"]');
  const st = getState();
  for (const card of [...cards]) {
    const habitId = (card as HTMLElement).dataset['habitId'];
    if (!habitId) continue;
    const { planState } = planFor(habitId, st);
    if (!planState || planState.state.planType !== 'interval') continue;
    const target = planState.state.targetIntervalMs;
    const elapsed = planState.state.lastUseAt ? Date.now() - new Date(planState.state.lastUseAt).getTime() : null;
    const remain = elapsed === null ? target : Math.max(0, target - elapsed);
    const ratio = elapsed === null ? 0 : Math.min(1, elapsed / target);
    const reached = remain === 0;

    const ring = card.querySelector('[data-role="ring"]') as HTMLElement | null;
    if (ring) ring.classList.toggle('reached', reached);
    const fill = card.querySelector('circle.fill') as SVGCircleElement | null;
    if (fill) {
      const c = Number(fill.dataset['circ'] ?? '0');
      fill.setAttribute('stroke-dashoffset', String(c * (1 - ratio)));
    }
    const time = card.querySelector('[data-live="time"]') as HTMLElement | null;
    if (time) time.textContent = reached ? 'GO' : compactDuration(remain);
    const note = card.querySelector('[data-live="note"]') as HTMLElement | null;
    if (note && planState.state.lastUseAt) note.textContent = `Elapsed ${formatDuration(elapsed ?? 0)}`;
  }
}

async function onLog(
  habit: HabitRecord,
  plan: PlanRecord,
  ps: PlanStateRecord,
  kind: 'used' | 'waited' | 'urge',
  method: ConsumptionMethodRecord | undefined,
  occurredAt?: Date,
) {
  const st = getState();
  const tone = st.settings.tone;
  const now = occurredAt ?? new Date();
  if (occurredAt && occurredAt.getTime() > Date.now()) {
    toast('That time is in the future.');
    return;
  }

  if (kind === 'urge') {
    const ev = await logEvent({
      type: 'urge_logged', habitId: habit.id, planId: plan.id,
      occurredAt: now.toISOString(), localDate: localDate(now),
      timezoneOffsetMinutes: tzOffsetMinutes(now), payload: {},
    });
    showUndoToast({
      message: copy('urge_logged', tone),
      onUndo: async () => {
        await markEventUndone(ev.id, 'user_undo');
        toast('Urge log undone.');
        window.dispatchEvent(new CustomEvent('wean:state-changed'));
      },
    });
    return;
  }

  if (kind === 'used') {
    // Snapshot plan state so undo can revert mutations made by engines below.
    const priorPlanState: PlanStateRecord = JSON.parse(JSON.stringify(ps)) as PlanStateRecord;
    let explanation = 'Logged.';
    let toneKey: ToneKey | null = null;
    const methodPayload: UsageLoggedPayloadV2 = method
      ? {
          methodId: method.id,
          methodKind: method.kind,
          logMode: method.defaultLogMode,
          domainUnits: method.defaultDomainUnits,
          quantity: 1,
          sessionId: uuid(),
          source: 'today_quick_action',
          ...(method.defaultSessionMinutes !== undefined ? { defaultSessionMinutes: method.defaultSessionMinutes } : {}),
          ...(method.mergeWindowMinutes !== undefined ? { mergeWindowMinutes: method.mergeWindowMinutes } : {}),
        }
      : { source: 'today_quick_action' };

    // Pull a few hours of recent events to feed the normalizer when
    // classifying this new tap (so vape merge windows are honored).
    const sinceIso = new Date(now.getTime() - 6 * 3600_000).toISOString();
    const recentEvents = (await eventsStore.byHabit(habit.id))
      .filter((e) => e.type === 'usage_logged' && e.occurredAt >= sinceIso);
    const allMethods = await consumptionMethods.byHabit(habit.id);
    const classified = classifyNewUsage({
      occurredAt: now,
      habitId: habit.id,
      method,
      recentEvents,
      methods: allMethods,
    });

    let createdEvent: import('../core/types.js').AppEvent | null = null;
    if (ps.state.planType === 'interval') {
      if (classified.merged) {
        const adjusted = intervalExtendSession(ps.state, classified.effectiveEndAt);
        await planStates.put({ ...ps, state: adjusted, updatedAt: nowIso() });
        explanation = 'Counted as part of the active session.';
      } else {
        const r = intervalApplyNormalized(ps.state, classified.effectiveStartAt, classified.effectiveEndAt, plan.pace);
        explanation = r.explanation;
        toneKey = r.outcome === 'stretch_success' ? 'stretch'
          : r.outcome === 'success' ? 'success'
          : r.outcome === 'near_miss' ? 'near_miss'
          : r.outcome === 'early_slip' ? 'early_slip'
          : null;
        await planStates.put({ ...ps, state: r.state, updatedAt: nowIso() });
      }
      createdEvent = await logEvent({
        type: 'usage_logged', habitId: habit.id, planId: plan.id,
        occurredAt: now.toISOString(), localDate: localDate(now),
        timezoneOffsetMinutes: tzOffsetMinutes(now),
        payload: {
          merged: classified.merged,
          effectiveStartAt: classified.effectiveStartAt.toISOString(),
          effectiveEndAt: classified.effectiveEndAt.toISOString(),
          ...methodPayload,
        },
      });
    } else if (ps.state.planType === 'window') {
      const r = windowApply(ps.state, classified.effectiveStartAt);
      explanation = r.explanation;
      await planStates.put({ ...ps, state: r.state, updatedAt: nowIso() });
      createdEvent = await logEvent({
        type: 'usage_logged', habitId: habit.id, planId: plan.id,
        occurredAt: now.toISOString(), localDate: localDate(now),
        timezoneOffsetMinutes: tzOffsetMinutes(now),
        payload: {
          outcome: r.outcome,
          firstUseMinutes: minutesSinceLocalMidnight(classified.effectiveStartAt),
          merged: classified.merged,
          ...methodPayload,
        },
      });
    } else {
      // Quota: count domain units only when not merging into an existing session.
      const units = classified.merged ? 0 : classified.domainUnits;
      const r = quotaApplyNormalized(ps.state, plan.pace, now, units);
      explanation = r.explanation;
      await planStates.put({ ...ps, state: r.state, updatedAt: nowIso() });
      createdEvent = await logEvent({
        type: 'usage_logged', habitId: habit.id, planId: plan.id,
        occurredAt: now.toISOString(), localDate: localDate(now),
        timezoneOffsetMinutes: tzOffsetMinutes(now),
        payload: {
          used: r.state.usedThisPeriod,
          cap: r.state.currentQuota,
          over: r.over,
          merged: classified.merged,
          domainUnits: units,
          ...methodPayload,
        },
      });
    }
    if (ps.secondaryQuota) {
      const fresh = await planStates.get(plan.id);
      if (fresh) {
        const units = classified.merged ? 0 : classified.domainUnits;
        const after = applySecondaryUsage(fresh, now, units);
        await planStates.put(after.record);
        if (after.over) toast('Weekly cap exceeded — try a rescue before the next.');
      }
    }
    const backfillSuffix = occurredAt && Date.now() - occurredAt.getTime() > 60_000
      ? ` (${relativeAgo(Date.now() - occurredAt.getTime())} ago)`
      : '';
    const msg = (toneKey ? `${copy(toneKey, tone)} — ${explanation}` : explanation) + backfillSuffix;
    if (createdEvent) {
      const eventId = createdEvent.id;
      showUndoToast({
        message: msg,
        onUndo: async () => {
          await markEventUndone(eventId, 'user_undo');
          await planStates.put({ ...priorPlanState, updatedAt: nowIso() });
          toast('Log undone.');
          window.dispatchEvent(new CustomEvent('wean:state-changed'));
        },
      });
    } else {
      toast(msg);
    }
    window.dispatchEvent(new CustomEvent('wean:state-changed'));
    return;
  }

  const waitedEvent = await logEvent({
    type: 'success_logged', habitId: habit.id, planId: plan.id,
    occurredAt: now.toISOString(), localDate: localDate(now),
    timezoneOffsetMinutes: tzOffsetMinutes(now),
    payload: { kind: 'waited' },
  });
  showUndoToast({
    message: copy('wait_acknowledged', tone),
    onUndo: async () => {
      await markEventUndone(waitedEvent.id, 'user_undo');
      toast('Wait undone.');
      window.dispatchEvent(new CustomEvent('wean:state-changed'));
    },
  });
}
